import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { dayjs } from "@/core/utils/date";
import { INLINE_CONTROL } from "@/profitability/constants";
import type { OverviewRun } from "@/profitability/overview/types";

const ranAt = (run: OverviewRun) =>
  dayjs(run.completed_at ?? run.created_at).format("MMM D, h:mm a");

/**
 * The run the period list names the period by, even while an earlier run of
 * it is on screen or its own workbook is unavailable.
 */
export const periodResultId = (
  runs: OverviewRun[] | undefined,
  shown: string,
) => runs?.find((run) => run.result)?.run_id ?? shown;

/**
 * When the period on screen ran, and every other time it ran: the newest is
 * the period's result, and an earlier one opens in its place to look back at.
 */
export const RunSelect = ({
  runs,
  current,
  onChange,
}: {
  /** The period's runs, newest first. */
  runs: OverviewRun[];
  current: string;
  /** The run picked; the result is the period itself. */
  onChange: (runId: string) => void;
}) => {
  const shown = runs.find((run) => run.run_id === current);
  const earlier = !!shown && !shown.result;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="link"
            color="neutral"
            size="sm"
            className={INLINE_CONTROL}
          />
        }
      >
        {shown
          ? `${earlier ? "Earlier run from" : "Ran"} ${ranAt(shown)}`
          : "Runs"}
        <CaretDownIcon aria-hidden className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-72 min-w-56 whitespace-nowrap"
      >
        {runs.map((run) => (
          <DropdownMenuItem
            key={run.run_id}
            onClick={() => onChange(run.run_id)}
            className="justify-between gap-4 tabular-nums"
          >
            <span>
              {ranAt(run)}
              {run.result && <span className="text-subtle"> · Newest</span>}
            </span>
            {run.run_id === current && (
              <CheckIcon aria-hidden className="size-3.5" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
